// Content moderation utility for filtering inappropriate content
const badWords = [
  // Profanity
  'damn', 'hell', 'shit', 'fuck', 'ass', 'bitch', 'bastard', 'crap',
  'piss', 'dick', 'cock', 'pussy', 'slut', 'whore', 'fag', 'nigger',
  'retard', 'faggot', 'cunt', 'twat', 'prick', 'asshole', 'motherfucker',
  
  // Adult/Sexual content
  'sex', 'porn', 'xxx', 'nude', 'naked', 'orgasm', 'masturbate', 
  'blowjob', 'handjob', 'anal', 'oral', 'cumshot', 'horny', 'sexy',
  'erotic', 'sexual', 'dildo', 'vibrator', 'fetish', 'kinky',
  
  // Violence/Abuse
  'kill', 'murder', 'rape', 'abuse', 'violence', 'hurt', 'harm',
  'attack', 'assault', 'torture', 'beating', 'stabbing', 'shooting',
  'suicide', 'death', 'dead', 'destroy', 'hate', 'hitler', 'nazi',
  
  // Drugs
  'cocaine', 'heroin', 'weed', 'marijuana', 'cannabis', 'meth', 
  'ecstasy', 'lsd', 'mdma', 'crack', 'drug', 'drugs', 'high',
  'stoned', 'drunk', 'alcohol', 'beer', 'vodka', 'whiskey',
  
  // Inappropriate terms
  'adult', 'dating', 'hookup', 'escort', 'prostitute', 'brothel',
  'casino', 'gambling', 'bet', 'poker', 'blackjack'
];

const inappropriatePatterns = [
  // Common phrases
  /\b(adult\s*content)\b/i,
  /\b(sexual\s*activity)\b/i,
  /\b(drug\s*deal)\b/i,
  /\b(buy\s*drugs)\b/i,
  /\b(sell\s*drugs)\b/i,
  /\b(adult\s*services)\b/i,
  /\b(escort\s*service)\b/i,
  /\b(dating\s*site)\b/i,
  /\b(hookup\s*app)\b/i,
  /\b(gambling\s*site)\b/i,
  /\b(online\s*casino)\b/i,
  /\b(quick\s*money)\b/i,
  /\b(easy\s*cash)\b/i,
  /\b(get\s*rich)\b/i,
  /\b(make\s*money\s*fast)\b/i,
  
  // URLs and contact info patterns
  /\b\w+\.(com|net|org|info|biz)\b/i,
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/, // Phone numbers
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/, // Email addresses
  /\b(whatsapp|telegram|kik|snapchat|instagram|facebook)\b/i,
  
  // Spam patterns
  /(.)\1{5,}/, // Repeated characters
  /\b(click\s*here)\b/i,
  /\b(visit\s*now)\b/i,
  /\b(limited\s*time)\b/i,
  /\b(act\s*now)\b/i,
  /\b(free\s*money)\b/i,
  /\b(100%\s*guaranteed)\b/i
];

const variations: { [key: string]: string[] } = {
  'fuck': ['f*ck', 'f**k', 'fck', 'fuk', 'phuck', 'f u c k', 'fook', 'fvck'],
  'shit': ['sh*t', 'sh**', 'sht', 'shyt', 's h i t', 'shiet', 'shite'],
  'damn': ['d*mn', 'd**n', 'dmn', 'darn', 'dam'],
  'bitch': ['b*tch', 'b**ch', 'btch', 'biatch', 'beatch'],
  'ass': ['a*s', 'a**', 'azz', '@ss', 'arse'],
  'sex': ['s*x', 's**', 'sexx', 'secks'],
  'kill': ['k*ll', 'kil', 'kyll'],
  'drug': ['dr*g', 'dr**', 'drugz'],
  'adult': ['ad*lt', 'ad**t', 'adul+', '@dult'],
  'abuse': ['ab*se', 'ab**e', 'abvse', '@buse']
};

// Common words that should never be flagged (whitelist)
const allowedWords = [
  'hello', 'help', 'shell', 'well', 'tell', 'sell', 'bell', 'cell', 'fell', 'yell',
  'analysis', 'assassin', 'assessment', 'class', 'pass', 'grass', 'mass', 'glass',
  'classic', 'assistance', 'passage', 'message', 'assemble', 'assume', 'associate',
  'killing', 'billing', 'filling', 'willing', 'chilling', 'drilling', 'thriller',
  'skill', 'mill', 'pill', 'hill', 'still', 'will', 'till', 'kill', 'bill'
];

export const containsProfanity = (text: string): boolean => {
  const lowercaseText = text.toLowerCase();
  
  // Remove special characters and extra spaces for better detection
  const cleanText = lowercaseText
    .replace(/[^a-z0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Check if the entire text is in the allowed words list
  if (allowedWords.includes(cleanText)) {
    return false;
  }
  
  // Check direct matches (whole words only with word boundaries)
  const words = cleanText.split(' ');
  for (const word of words) {
    // Skip if word is in allowed list
    if (allowedWords.includes(word)) {
      continue;
    }
    
    if (badWords.includes(word)) {
      return true;
    }
  }
  
  // Use regex with word boundaries for more precise matching
  for (const word of badWords) {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(cleanText)) {
      return true;
    }
  }
  
  // Check inappropriate patterns
  for (const pattern of inappropriatePatterns) {
    if (pattern.test(text)) {
      return true;
    }
  }
  
  // Check variations with word boundaries
  for (const [baseWord, varList] of Object.entries(variations)) {
    for (const variation of varList) {
      const regex = new RegExp(`\\b${variation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (regex.test(cleanText)) {
        return true;
      }
    }
  }
  
  // Check for words with numbers/symbols replacing letters (enhanced)
  const leetSpeak = cleanText
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/8/g, 'b')
    .replace(/@/g, 'a')
    .replace(/!/g, 'i')
    .replace(/\+/g, 't');
    
  for (const word of badWords) {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(leetSpeak)) {
      return true;
    }
  }
  
  return false;
};

export interface ModerationResult {
  isClean: boolean;
  message?: string;
}

export const moderateContent = (text: string): ModerationResult => {
  // Check for inappropriate content first
  if (containsProfanity(text)) {
    return {
      isClean: false,
      message: 'Your comment contains inappropriate content. Please keep discussions educational and respectful.'
    };
  }
  
  // Check for contact information sharing
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
  const phonePattern = /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/;
  if (emailPattern.test(text) || phonePattern.test(text)) {
    return {
      isClean: false,
      message: 'Please do not share personal contact information in comments.'
    };
  }
  
  // Check for URLs (simple detection)
  const urlPattern = /\b\w+\.(com|net|org|info|biz|edu)\b/i;
  if (urlPattern.test(text)) {
    return {
      isClean: false,
      message: 'Please do not share external links in comments.'
    };
  }
  
  // Check for excessive caps (shouting)
  const capsCount = (text.match(/[A-Z]/g) || []).length;
  const totalLetters = (text.match(/[A-Za-z]/g) || []).length;
  if (totalLetters > 10 && capsCount / totalLetters > 0.7) {
    return {
      isClean: false,
      message: 'Please avoid using excessive capital letters.'
    };
  }
  
  // Check for spam-like content (repeated characters)
  if (/(.)\1{4,}/.test(text)) {
    return {
      isClean: false,
      message: 'Please avoid using excessive repeated characters.'
    };
  }
  
  // Check for very short/meaningless content
  if (text.trim().length < 2) {
    return {
      isClean: false,
      message: 'Please write a meaningful comment.'
    };
  }
  
  return { isClean: true };
};